import React from 'react';
import Text from '@service-victoria/component-library/components/Text';
import { ExternalLinks } from 'utils/constants';
import * as Styled from '../styled';

type BeforeYouStartProps = Record<string, never>;

const BeforeYouStart: React.FC<BeforeYouStartProps> = () => (
  <>
    <Text.Heading.H3 fontWeight={600}>International passenger arrivals permit</Text.Heading.H3>
    <Styled.Spacer16 />
    <Text.Body>
      You need to apply for a permit to enter Victoria if you have been overseas in the past 14
      days, including if you are entering Victoria from another Australian state or territory.
    </Text.Body>
    <Text.Body>
      If you are entering Victoria from another Australian state or territory, you must comply with
      all the remaining requirements that are applicable relative to the time you arrived in
      Australia.
    </Text.Body>
    <Text.Body>
      You will need to quarantine until you receive a negative result from a COVID-19 PCR or RA test
      taken within 24 hours of entering Victoria.
    </Text.Body>
    <Text.Body>You must remain in quarantine during this time unless you are:</Text.Body>
    <Styled.List>
      <Styled.ListItem>obtaining medical care or medical supplies</Styled.ListItem>
      <Styled.ListItem>getting a COVID-19 PCR or RA test</Styled.ListItem>
      <Styled.ListItem>in an emergency</Styled.ListItem>
      <Styled.ListItem>required by law</Styled.ListItem>
      <Styled.ListItem>departing the State of Victoria.</Styled.ListItem>
    </Styled.List>
    <Text.Body>
      While in quarantine you must not share bedrooms, bathrooms or any other facilities with people
      who aren’t travelling with you.
    </Text.Body>
    <Text.Body>
      This helps reduce the risk of COVID-19 in Victoria and manage any outbreaks.{' '}
    </Text.Body>
    <Text.Body>
      If you&apos;re helping someone else to apply, they need to understand and agree to the
      conditions.
    </Text.Body>
    <Text.Body>You can apply for an international passenger arrivals permit if you are:</Text.Body>
    <Styled.List>
      <Styled.ListItem>fully vaccinated or have a medical exemption</Styled.ListItem>
      <Styled.ListItem>
        above 12 years and 2 months of age and below 18 years of age and travelling alone, or with
        at least one parent or guardian who is fully vaccinated or medically exempt
      </Styled.ListItem>
      <Styled.ListItem>under 12 years and 2 months of age and travelling alone.</Styled.ListItem>
    </Styled.List>
    <Text.Body>
      International arrivals will need to demonstrate their vaccination status to the Australian
      Federal Government with a{' '}
      <Text.Link target='_blank' href={ExternalLinks.VaccinedApprovedTga}>
        vaccine approved or recognised
      </Text.Link>{' '}
      by the Therapeutic Goods Administration.
    </Text.Body>
    <Text.Body>
      You’ll need to supply proof of vaccination or{' '}
      <Text.Link
        target='_blank'
        href={ExternalLinks.CovidMedicalExemption}
        value='proof of a medical exemption'
      />
      , if asked. Fines apply if you give false information.
    </Text.Body>
    <Text.Body>
      The Australian Federal Government also requires you to present evidence of a negative COVID-19
      PCR or RA test taken within 3 days of your flight’s scheduled departure to your airline when
      you check-in for your flight.
    </Text.Body>
    <Text.Body>
      People entering Victoria who have been overseas in the past 14 days will be required to get a
      COVID-19 PCR or RA test within 24 hours of arriving in Victoria (unless it is more than 24
      hours since arriving in Australia or you have already taken a test since arriving in
      Australia).
    </Text.Body>
    <Text.Body>
      If you are not fully vaccinated and don’t have a medical exemption, you can’t apply for an
      international passenger arrival permit to enter Victoria. You will need to complete 14 days
      hotel quarantine on arrival,{' '}
      <Text.Link target='_blank' href={ExternalLinks.InternationalHealthAlert}>
        learn more
      </Text.Link>
      .
    </Text.Body>
    <Styled.Spacer16 />

    <Text.Heading.H3 fontWeight={600}>Exemptions</Text.Heading.H3>
    <Text.Body>
      If you can’t comply with the conditions of the international passenger arrivals permit, you
      must apply for an exemption.
    </Text.Body>
    <Text.Body>Exemptions are only granted in exceptional circumstances.</Text.Body>
    <Styled.Spacer16 />

    <Text.Heading.H3 fontWeight={600}>Protecting your privacy</Text.Heading.H3>
    <Text.Body>
      Service Victoria will pass on the information you give to the Victorian Department of Health
      for outbreak control and contact tracing use, or to assess your request for an exemption. We
      won’t use your information for any other purpose unless authorised by law.{' '}
      <Text.Link
        target='_blank'
        value='Learn more.'
        href='https://service.vic.gov.au/covid-19/border-entry-permit-collection-notice'
      />
    </Text.Body>
    <Text.Body>
      For more information, call the 24/7 coronavirus hotline{' '}
      <Text.Link href='tel:1800675398' value='1800 675 398' />.
    </Text.Body>
  </>
);

export default BeforeYouStart;
